import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import {
  clearMcpOAuthState,
  getPendingMcpOAuthState,
  isSafeMcpClientRedirect,
  isWebMcpClientRedirect,
} from 'app/modules/mcp/hooks/useMcpOAuthFlow';
import { useCompleteMcpOAuth } from 'app/modules/mcp/queries/useCompleteMcpOAuth';
import { selectMcpServerEnabled } from 'app/shared/featureFlags/selectors';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import {
  U21Button,
  U21Card,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';

export const McpConsentPage = () => {
  const history = useHistory();
  const location = useLocation();
  const mcpEnabled = useSelector(selectMcpServerEnabled);

  const {
    mutateAsync: completeMcpOAuth,
    isPending,
    isSuccess,
    data,
    variables,
  } = useCompleteMcpOAuth();

  const oauthState = useMemo(
    () => getPendingMcpOAuthState(location.search),
    [location.search],
  );

  // Clear any persisted state when leaving the page so an abandoned flow doesn't
  // bounce the agent back to consent on their next login.
  useEffect(() => () => clearMcpOAuthState(), []);

  const handleDecision = async (approved: boolean) => {
    if (!oauthState) {
      return;
    }
    try {
      await completeMcpOAuth({ state: oauthState, approved });
      // Drop the state param so a refresh doesn't re-submit; the mutation result
      // drives the confirmation view below.
      clearMcpOAuthState();
      history.replace(ROUTES_MAP.mcpConsent.path);
    } catch {
      // Error toast is handled by the mutation's onError.
    }
  };

  const handoffToClient = (redirectUrl: string) => {
    if (!isSafeMcpClientRedirect(redirectUrl)) {
      return;
    }
    window.location.assign(redirectUrl);
  };

  if (!mcpEnabled) {
    return (
      <PageContainer>
        <ConsentCard title="MCP not enabled">
          <U21Typography>
            MCP server is not enabled for this organization.
          </U21Typography>
        </ConsentCard>
      </PageContainer>
    );
  }

  if (isSuccess) {
    const approved = Boolean(variables?.approved);
    const redirectUrl = data?.redirect_url || undefined;
    const safeRedirectUrl =
      redirectUrl && isSafeMcpClientRedirect(redirectUrl)
        ? redirectUrl
        : undefined;
    return (
      <PageContainer>
        <ConsentCard
          title={approved ? 'Connection approved' : 'Connection denied'}
        >
          <U21Spacer spacing={2}>
            <U21Typography>
              {approved
                ? "You've approved access for your MCP client. Return to your client to finish connecting."
                : "You've denied this MCP connection request."}
            </U21Typography>
            {redirectUrl && !safeRedirectUrl && (
              <U21Typography color="text.secondary">
                Unable to open the MCP client automatically. Start the
                connection again from your client.
              </U21Typography>
            )}
            {safeRedirectUrl && !isWebMcpClientRedirect(safeRedirectUrl) && (
              <U21Typography color="text.secondary">
                You can close this tab and switch back to your MCP client.
              </U21Typography>
            )}
            <Actions horizontal>
              <U21Button
                color="primary"
                to={ROUTES_MAP.home.path}
                variant="text"
              >
                Go to Unit21
              </U21Button>
              {safeRedirectUrl && (
                <U21Button
                  color="primary"
                  onClick={() => handoffToClient(safeRedirectUrl)}
                  variant="contained"
                >
                  {isWebMcpClientRedirect(safeRedirectUrl)
                    ? 'Continue to MCP client'
                    : 'Return to MCP client'}
                </U21Button>
              )}
            </Actions>
          </U21Spacer>
        </ConsentCard>
      </PageContainer>
    );
  }

  if (!oauthState) {
    return (
      <PageContainer>
        <ConsentCard title="Missing authorization request">
          <U21Typography>
            No pending MCP authorization was found. Start the connection from
            your MCP client again.
          </U21Typography>
        </ConsentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ConsentCard title="Connect Unit21 MCP">
        <U21Spacer spacing={2}>
          <U21Typography>
            An MCP client is requesting access to your Unit21 agent session.
            Approve only if you initiated this connection.
          </U21Typography>
          <Actions horizontal>
            <U21Button
              disabled={isPending}
              onClick={() => handleDecision(false)}
              variant="outlined"
            >
              Deny
            </U21Button>
            <U21Button
              color="primary"
              disabled={isPending}
              onClick={() => handleDecision(true)}
              variant="contained"
            >
              Approve
            </U21Button>
          </Actions>
        </U21Spacer>
      </ConsentCard>
    </PageContainer>
  );
};

const PageContainer = styled(U21Spacer)`
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(12)};
  padding: ${({ theme }) => theme.spacing(2)};
`;

const ConsentCard = styled(U21Card)`
  max-width: 520px;
  width: 100%;
`;

const Actions = styled(U21Spacer)`
  justify-content: flex-end;
`;
